import { useCallback, useEffect, useRef, useState } from 'react';
import { TitlePage, TitlePageCursor, titlesService } from '../services/firebaseFunctions';

const DEFAULT_PAGE_SIZE = 10;
const emptyPage: TitlePage = { items: [], nextCursor: null, totalCount: 0 };

export const useTitlePagination = () => {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [publisherIds, setPublisherIds] = useState<string[]>([]);
  const [includeWithoutPublisher, setIncludeWithoutPublisher] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [data, setData] = useState<TitlePage>(emptyPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursors, setCursors] = useState<Record<number, TitlePageCursor | undefined>>({ 0: undefined });
  const activeRequest = useRef(0);

  const cursor = cursors[page];

  const load = useCallback(async () => {
    const requestId = ++activeRequest.current;
    try {
      setLoading(true);
      setError(null);
      const result = await titlesService.listPage({
        pageSize,
        cursor,
        search: search.trim() || undefined,
        ...(publisherIds.length > 0 ? { publisherIds } : {}),
        ...(includeWithoutPublisher ? { includeWithoutPublisher: true } : {}),
      });
      if (requestId !== activeRequest.current) return;
      setData(result);

      const nextCursor = result.nextCursor;
      if (nextCursor) {
        setCursors((current) => {
          const known = current[page + 1];
          if (known?.id === nextCursor.id && known.name === nextCursor.name) return current;
          return { ...current, [page + 1]: nextCursor };
        });
      }
    } catch (caught) {
      if (requestId !== activeRequest.current) return;
      setError(caught instanceof Error ? caught.message : 'Erro ao carregar títulos');
    } finally {
      if (requestId === activeRequest.current) setLoading(false);
    }
  }, [cursor, includeWithoutPublisher, page, pageSize, publisherIds, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetPagination = () => {
    activeRequest.current += 1;
    setPage(0);
    setCursors({ 0: undefined });
    setData(emptyPage);
    setError(null);
    setLoading(true);
  };

  const updateSearch = (value: string) => {
    resetPagination();
    setSearch(value);
  };

  const updatePublisherIds = (value: string[]) => {
    resetPagination();
    setPublisherIds(value);
  };

  const updateIncludeWithoutPublisher = (value: boolean) => {
    resetPagination();
    setIncludeWithoutPublisher(value);
  };

  const updatePageSize = (value: number) => {
    resetPagination();
    setPageSize(value);
  };

  return {
    ...data,
    page,
    pageSize,
    search,
    publisherIds,
    includeWithoutPublisher,
    loading,
    error,
    refresh: load,
    next: () => data.nextCursor && setPage((current) => current + 1),
    previous: () => setPage((current) => Math.max(0, current - 1)),
    setSearch: updateSearch,
    setPublisherIds: updatePublisherIds,
    setIncludeWithoutPublisher: updateIncludeWithoutPublisher,
    setPageSize: updatePageSize,
  };
};
